import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from '@axios'
import { getNamespaceSocket } from '@/composables/useSocket'

function _emitViewConversation(conversationId) {
  const sock = getNamespaceSocket('/messaging')
  if (sock && sock.connected) {
    sock.emit('support_viewing_conversation', { conversationId })
  }
}

function _emitLeaveConversation() {
  const sock = getNamespaceSocket('/messaging')
  if (sock && sock.connected) {
    sock.emit('support_leave_conversation')
  }
}

export const useSupportStore = defineStore('support', () => {
  // ─── Member state ──────────────────────────────────────────────────────────
  const conversation = ref(null)
  const messages = ref([])
  const memberUnreadCount = ref(0)
  const loadingMessages = ref(false)
  const sendingMessage = ref(false)

  // ─── Admin state ───────────────────────────────────────────────────────────
  const adminConversations = ref([])
  const adminActiveConversationId = ref(null)
  const adminMessages = ref([])
  const adminTotalUnread = ref(0)
  const memberContext = ref(null)
  const loadingAdminConversations = ref(false)
  const loadingAdminMessages = ref(false)
  const loadingMemberContext = ref(false)

  const adminActiveConversation = computed(() =>
    adminConversations.value.find(c => c._id === adminActiveConversationId.value) || null
  )

  // ─── Member actions ────────────────────────────────────────────────────────

  async function getOrCreateSupportConversation() {
    try {
      const { data } = await axios.post('/support/conversations')
      conversation.value = data.conversation
      
      return data.conversation
    } catch (err) {
      console.error('getOrCreateSupportConversation error', err)
      throw err
    }
  }

  async function loadMessages(page = 1) {
    loadingMessages.value = true
    try {
      if (!conversation.value) {
        await getOrCreateSupportConversation()
      }
      if (!conversation.value) return

      const convId = conversation.value._id
      _emitViewConversation(convId)

      const { data } = await axios.get(`/support/conversations/${convId}`, {
        params: { page },
      })

      if (page === 1) {
        messages.value = data.messages || []
      } else {
        messages.value = [...(data.messages || []), ...messages.value]
      }

      // If response includes conversation (member endpoint), update it
      if (data.conversation) {
        conversation.value = data.conversation
      }

      memberUnreadCount.value = 0
    } catch (err) {
      console.error('loadMessages error', err)
    } finally {
      loadingMessages.value = false
    }
  }

  function _pushMessageIfNew(msg) {
    if (!msg) return
    const exists = messages.value.some(m => m._id === msg._id)
    if (!exists) messages.value.push(msg)
  }

  function _pushAdminMessageIfNew(msg) {
    if (!msg) return
    const exists = adminMessages.value.some(m => m._id === msg._id)
    if (!exists) adminMessages.value.push(msg)
  }

  async function _sendViaSocket(text, imageIds) {
    const sock = getNamespaceSocket('/messaging')
    if (!sock?.connected) return null

    return new Promise((resolve, reject) => {
      sock.emit('support_send_message', { text, imageIds }, response => {
        if (response.error) reject(new Error(response.error))
        else resolve(response.message)
      })
    })
  }

  async function _sendViaRest(text, imageIds) {
    if (!conversation.value) {
      await getOrCreateSupportConversation()
    }
    const convId = conversation.value._id
    const { data } = await axios.post(`/support/conversations/${convId}/messages`, { text, imageIds })

    return data.message
  }

  async function sendMessage({ text, imageIds } = {}) {
    sendingMessage.value = true
    try {
      const sock = getNamespaceSocket('/messaging')
      const msg = sock?.connected
        ? await _sendViaSocket(text, imageIds)
        : await _sendViaRest(text, imageIds)

      _pushMessageIfNew(msg)

      return msg
    } catch (err) {
      console.error('sendMessage error', err)
      throw err
    } finally {
      sendingMessage.value = false
    }
  }

  async function markRead() {
    try {
      if (!conversation.value) return
      await axios.put(`/support/conversations/${conversation.value._id}/read`)
      memberUnreadCount.value = 0
    } catch (err) {
      console.error('markRead error', err)
    }
  }

  async function fetchMemberUnreadCount() {
    try {
      const { data } = await axios.get('/support/unread-count')
      memberUnreadCount.value = data.count || 0
    } catch (err) {
      // Silently fail
    }
  }

  // ─── Admin actions ─────────────────────────────────────────────────────────

  async function fetchAdminConversations() {
    loadingAdminConversations.value = true
    try {
      const { data } = await axios.get('/support/conversations')
      adminConversations.value = data.conversations || []
      fetchAdminTotalUnread()
    } catch (err) {
      console.error('fetchAdminConversations error', err)
    } finally {
      loadingAdminConversations.value = false
    }
  }

  async function loadAdminConversation(convId) {
    adminActiveConversationId.value = convId
    _emitViewConversation(convId)
    loadingAdminMessages.value = true
    try {
      const { data } = await axios.get(`/support/conversations/${convId}`)
      adminMessages.value = data.messages || []

      // Mark as read locally
      const conv = adminConversations.value.find(c => c._id === convId)
      if (conv && conv.unread > 0) {
        conv.unread = 0
      }

      // Mark read on server
      await axios.put(`/support/conversations/${convId}/read`)
      fetchAdminTotalUnread()
    } catch (err) {
      console.error('loadAdminConversation error', err)
    } finally {
      loadingAdminMessages.value = false
    }
  }

  async function _sendAdminViaSocket(convId, text, imageIds) {
    const sock = getNamespaceSocket('/messaging')
    if (!sock?.connected) return null

    return new Promise((resolve, reject) => {
      sock.emit('support_admin_send_message', { conversationId: convId, text, imageIds }, response => {
        if (response.error) reject(new Error(response.error))
        else resolve(response.message)
      })
    })
  }

  async function sendAdminMessage(convId, { text, imageIds } = {}) {
    sendingMessage.value = true
    try {
      const sock = getNamespaceSocket('/messaging')
      const msg = sock?.connected
        ? await _sendAdminViaSocket(convId, text, imageIds)
        : (await axios.post(`/support/conversations/${convId}/messages`, { text, imageIds })).data.message

      _pushAdminMessageIfNew(msg)
      if (msg) _updateAdminConversationLastMessage(convId, msg)

      return msg
    } catch (err) {
      console.error('sendAdminMessage error', err)
      throw err
    } finally {
      sendingMessage.value = false
    }
  }

  async function markReadAdmin(convId) {
    try {
      await axios.put(`/support/conversations/${convId}/read`)
      const conv = adminConversations.value.find(c => c._id === convId)
      if (conv) conv.unread = 0
      fetchAdminTotalUnread()
    } catch (err) {
      console.error('markReadAdmin error', err)
    }
  }

  async function markUnreadAdmin(convId) {
    try {
      await axios.put(`/support/conversations/${convId}/unread`)
      const conv = adminConversations.value.find(c => c._id === convId)
      if (conv) conv.unread = 1
      fetchAdminTotalUnread()
    } catch (err) {
      console.error('markUnreadAdmin error', err)
    }
  }

  async function fetchAdminTotalUnread() {
    try {
      const { data } = await axios.get('/support/unread-count')
      adminTotalUnread.value = data.count || 0
    } catch (err) {
      // Silently fail
    }
  }

  async function fetchMemberContext(convId) {
    loadingMemberContext.value = true
    try {
      const { data } = await axios.get(`/support/conversations/${convId}/context`)
      memberContext.value = data.context || null
    } catch (err) {
      console.error('fetchMemberContext error', err)
    } finally {
      loadingMemberContext.value = false
    }
  }

  // ─── Socket-driven updates ────────────────────────────────────────────────

  function _handleMemberSideMessage(message, conversationId) {
    const isViewing = conversation.value && conversation.value._id === conversationId
    if (isViewing) {
      _pushMessageIfNew(message)
      if (message.senderType === 'support') markRead()
    } else if (message.senderType === 'support') {
      memberUnreadCount.value++
    }
  }

  function _handleAdminSideMessage(message, conversationId) {
    const isViewing = adminActiveConversationId.value === conversationId
    if (isViewing) {
      _pushAdminMessageIfNew(message)
      if (message.senderType === 'user') markReadAdmin(conversationId)
    } else if (message.senderType === 'user') {
      adminTotalUnread.value++
      const conv = adminConversations.value.find(c => c._id === conversationId)
      if (conv) conv.unread = (conv.unread || 0) + 1
    }
  }

  function handleSupportNewMessage(payload) {
    const { message, conversationId } = payload
    _handleMemberSideMessage(message, conversationId)
    _handleAdminSideMessage(message, conversationId)

    // Update last message in admin conversation list
    _updateAdminConversationLastMessage(conversationId, message)
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  function _updateAdminConversationLastMessage(convId, message) {
    const idx = adminConversations.value.findIndex(c => c._id === convId)
    if (idx !== -1) {
      adminConversations.value[idx] = {
        ...adminConversations.value[idx],
        lastMessage: {
          text: message.text || '[Image]',
          senderType: message.senderType,
          senderId: message.senderId,
          sentAt: message.createdAt,
        },
        lastMessageAt: message.createdAt,
      }

      // Move to top
      const conv = adminConversations.value.splice(idx, 1)[0]
      adminConversations.value.unshift(conv)
    }
  }

  function clearAdminActiveConversation() {
    _emitLeaveConversation()
    adminActiveConversationId.value = null
    adminMessages.value = []
    memberContext.value = null
  }

  function leaveSupportView() {
    _emitLeaveConversation()
  }

  function $reset() {
    conversation.value = null
    messages.value = []
    memberUnreadCount.value = 0
    adminConversations.value = []
    adminActiveConversationId.value = null
    adminMessages.value = []
    adminTotalUnread.value = 0
    memberContext.value = null
  }

  return {
    // Member
    conversation,
    messages,
    memberUnreadCount,
    loadingMessages,
    sendingMessage,
    getOrCreateSupportConversation,
    loadMessages,
    sendMessage,
    markRead,
    fetchMemberUnreadCount,

    // Admin
    adminConversations,
    adminActiveConversationId,
    adminActiveConversation,
    adminMessages,
    adminTotalUnread,
    memberContext,
    loadingAdminConversations,
    loadingAdminMessages,
    loadingMemberContext,
    fetchAdminConversations,
    loadAdminConversation,
    sendAdminMessage,
    markReadAdmin,
    markUnreadAdmin,
    fetchAdminTotalUnread,
    fetchMemberContext,

    // Socket
    handleSupportNewMessage,

    // Lifecycle
    clearAdminActiveConversation,
    leaveSupportView,
    $reset,
  }
})
